import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { registerConfirmSteps, registerScreenActions, registerScreenPanels } from '../../studio-nav/screen-panels.service';
import { ConfirmWaitsNgService } from '../../services/mcp/actions/confirm-waits-ng.service';
import type { Prefilled } from '../../services/mcp/actions/confirm-lane';
import type { ScreenActionAnswer } from '../../services/mcp/actions/fast-lane';
import { FormsModule } from '@angular/forms';
import { Page } from '../../model/page.model';
import { buildDefaultPage, isProtectedPage } from '../../model/page-defaults';
import { PageService } from '../../services/page.service';
import { Router } from '@angular/router';
import { EntityService } from '../../services/entity.service';
import { Entity, TypeData } from '../../model/entity.model';
import { firstValueFrom, Subscription } from 'rxjs';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { BlueprintService, BlueprintUsage } from '../../services/blueprint.service';
import { BlueprintContextBuilder } from '../../services/blueprint-context.builder';
import { ClientService } from '../../services/client.service';
import { AIGenerationTrackerService, GenerationTask } from '../../services/ai-generation-tracker.service';
import { pageUnwiredCount } from '../../shared/unwired/unwired.util';

/** The tabs above the page list. */
const PAGE_FILTERS = ['all', 'web', 'mobile', 'pdf'];

@Component({
    selector: 'app-pages',
    imports: [FormsModule, CommonModule],
    templateUrl: './pages.component.html',
    host: { class: 'block h-full' },
    styles: [`
    :host { display: block; height: 100%; }

    .custom-scroll::-webkit-scrollbar { width: 5px; height: 5px; }
    .custom-scroll::-webkit-scrollbar-track { background: transparent; }
    .custom-scroll::-webkit-scrollbar-thumb { border-radius: 3px; }

    .theme-light .custom-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.08); }
    .theme-light .custom-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.15); }
    .theme-dark .custom-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); }
    .theme-dark .custom-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.15); }

    /* ── AI generation animation (matches the landing-page gradient theme) ── */
    @keyframes genGradientShift { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
    @keyframes genTrace { to { stroke-dashoffset: 0; } }
    @keyframes genBlob { 0%, 100% { transform: translate(0,0) scale(1); opacity: .5; } 50% { transform: translate(5px,-5px) scale(1.2); opacity: .9; } }
    .gen-grad-text {
      background: linear-gradient(90deg, #f97316, #a855f7, #6366f1, #a855f7, #f97316);
      background-size: 300% 100%;
      -webkit-background-clip: text; background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: genGradientShift 4s ease infinite;
    }
    .gen-blob { filter: blur(12px); animation: genBlob 3s ease-in-out infinite; }
    .gen-logo path {
      fill: transparent;
      stroke: url(#genGrad);
      stroke-width: 2.5; stroke-linejoin: round; stroke-linecap: round;
      stroke-dasharray: 100; stroke-dashoffset: 100;
      animation: genTrace 1.8s ease-in-out infinite alternate;
    }
    .gen-logo path:nth-of-type(2) { animation-delay: .35s; }
    .gen-bar-fill {
      background: linear-gradient(90deg, #f97316, #a855f7, #6366f1);
      background-size: 200% 100%;
      animation: genGradientShift 3s ease infinite;
    }

    /* ── Cinematic full-screen generation overlay ── */
    @keyframes genOverlayIn { from { opacity: 0; } to { opacity: 1; } }
    /* 3D rings — continuously tumbling edge-on in depth (needs perspective on the parent) */
    @keyframes genTumbleA { from { transform: rotateX(12deg) rotateY(0deg); } to { transform: rotateX(12deg) rotateY(360deg); } }
    @keyframes genTumbleB { from { transform: rotateY(16deg) rotateX(0deg); } to { transform: rotateY(16deg) rotateX(360deg); } }
    @keyframes genShimmer { 0% { transform: translateX(-130%); } 100% { transform: translateX(360%); } }
    @keyframes genGlowPulse { 0%, 100% { opacity: .35; transform: scale(1); } 50% { opacity: .8; transform: scale(1.16); } }
    @keyframes genFloatY { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
    @keyframes genPop { 0% { transform: scale(1); } 45% { transform: scale(1.12); } 100% { transform: scale(1); } }
    /* Forge: the mark glows hot and the emitted embers fly off */
    @keyframes genHeat { 0%, 100% { filter: drop-shadow(0 0 10px rgba(249,115,22,0.55)); } 50% { filter: drop-shadow(0 0 24px rgba(168,85,247,0.85)); } }
    @keyframes genSpark {
      0%   { transform: translate(-50%, -50%) scale(1); opacity: 0; }
      12%  { opacity: 1; }
      100% { transform: translate(calc(-50% + var(--sx, 0px)), calc(-50% - 76px)) scale(0.2); opacity: 0; }
    }
    .gen-overlay { animation: genOverlayIn .45s ease-out; }
    .gen-stage-float { animation: genFloatY 5s ease-in-out infinite; perspective: 1100px; transform-style: preserve-3d; }
    .gen-glow { animation: genGlowPulse 2.6s ease-in-out infinite; }
    .gen-ring  { animation: genTumbleA 6s linear infinite; }
    .gen-ring-2 { animation: genTumbleB 7.5s linear infinite; }
    .gen-spark { animation: genSpark 1.5s ease-out infinite; }
    .gen-shimmer-sweep { animation: genShimmer 1.8s ease-in-out infinite; }
    .gen-pop { animation: genPop .8s cubic-bezier(0.16, 1, 0.3, 1); }
    .gen-logo-xl { animation: genHeat 2.2s ease-in-out infinite; }
    .gen-logo-xl path {
      fill: url(#genGradXl);
      stroke: #fff; stroke-width: 1.2; stroke-linejoin: round; stroke-linecap: round;
      stroke-dasharray: 100; stroke-dashoffset: 100;
      animation: genTrace 2.2s ease-in-out infinite alternate;
    }
    .gen-logo-xl path:nth-of-type(2) { animation-delay: .4s; }

    `]
})
export class PagesComponent implements OnInit, OnDestroy {
pageList: Page[] = [];
filteredPageList: Page[] = [];
  /**
   * page id -> count of links a cross-page paste could not carry.
   *
   * ⚠ PRECOMPUTED. This screen renders every page in the project, so a template-called walk would
   * run once per page per change-detection tick. `PageService.query` has already parsed every blob,
   * so filling this costs a walk over data that is in memory, once per list build.
   */
  unwiredByPage: Record<string, number> = {};
selectedFilter: string = 'all';
newPageName: string = '';
newPageType: string = 'web';
showCreateModal: boolean = false;
private panels = registerScreenPanels({
  screen: 'pages',
  loading: () => !this.pageListArrived,
  here: () => ({ tab: this.selectedFilter, ...(this.showCreateModal ? { panel: 'new-page' } : {}) }),
  holds: () => ({
    pages: this.filteredPageList.map((p) => ({ id: p.id, name: p.name, type: p.type })),
    filter: this.selectedFilter,
    searchTerm: this.searchTerm,
    newPageName: this.newPageName,
    newPageType: this.newPageType,
    pageId: this.deleteTarget?.id ?? '',
    deleteConfirmText: this.deleteConfirmText,
    failedTasks: this.failedTasks().map((t) => ({ taskId: t.pageName, message: t.message })),
    // Nothing is selected here: the id is chosen from failedTasks each time.
    taskId: '',
  }),
  select: (name) => {
    if (name === 'new-page') this.showCreateModal = true;
    else this.setFilter(name);
    return true;
  },
  // Was (2026-10-30), before the delete form reported itself: unsaved: () => (this.newPageHalfFilled() ? { count: 1, name: 'The new page' } : null),
  unsaved: () => {
    if (this.newPageHalfFilled()) return { count: 1, name: 'The new page' };
    return this.deleteTarget && this.deleteConfirmText.trim() ? { count: 1, name: `The delete form for ${this.deleteTarget.name}` } : null;
  },
});
private offers = registerScreenActions({
  'create-page': (values) => this.createPageFor(values),
  'search-pages': async (values) => {
    this.searchTerm = String(values['searchTerm'] ?? '').trim();
    this.applyFilter();
    return { done: this.searchTerm ? `Searched pages for ${this.searchTerm}.` : 'Cleared the page search.', result: { searchTerm: this.searchTerm, shown: this.filteredPageList.length } };
  },
  'filter-pages': async (values) => {
    const filter = String(values['filter'] ?? '');
    if (!PAGE_FILTERS.includes(filter)) return { refused: `Pages can be filtered by ${PAGE_FILTERS.join(', ')}. Nothing was changed.` };
    this.setFilter(filter);
    return { done: `Showing ${filter} pages.`, result: { filter, shown: this.filteredPageList.length } };
  },
  'reload-list': async () => {
    // The read fails quietly and leaves the old list up, so only its answer tells a reload from a failure.
    if (!(await this.pageService.query(1000))) throw new Error('The pages could not be read. The list on screen may be out of date.');
    return { done: 'Reloaded the pages.', result: { shown: this.filteredPageList.length } };
  },
  'dismiss-failed-task': async (values) => {
    const id = String(values['taskId'] ?? '');
    const task = this.generationTasks.get(id);
    if (!task || task.status !== 'failed') {
      const failed = this.failedTasks().map((t) => t.pageName);
      return { refused: `There's no failed task "${id}". ${failed.length ? `The failed ones are ${failed.join(', ')}.` : 'None have failed.'} Nothing was changed.` };
    }
    this.dismissFailedTask(id);
    return { done: `Dismissed the failed task for ${id}.`, result: { taskId: id } };
  },
});
private waits = inject(ConfirmWaitsNgService);
private confirmSteps = registerConfirmSteps({ 'delete-page': { press: (values) => this.prefillDelete(values) } });
/** Until the list arrives the name check has nothing to check against, and a page's id is its name, so a taken name would write over that page. */
private pageListArrived = false;
searchTerm: string = '';

// Theme & design
get darkMode(): boolean { return this.globalVariablesService.pageThemeDark; }
set darkMode(v: boolean) { this.globalVariablesService.pageThemeDark = v; }
selectedDesign: 'A' | 'B' | 'C' = 'A';
previewIndex = 0;
expandedIndex = -1;
collapsedGroups = new Set<string>();
// Delete confirmation
deleteTarget: Page | null = null;
deleteConfirmText: string = '';
deleteLoading: boolean = false;

// AI Blueprint
showAIModal: boolean = false;
aiScreenshot: string = '';
aiScreenshotName: string = '';
// Blueprint target: 'web' (full desktop page) or 'mobile' (393px phone frame). Screenshot path only.
blueprintPageType: 'web' | 'mobile' = 'web';
aiPageName: string = '';
aiPrompt: string = '';
private aiAbortController: AbortController | null = null;

// Cinematic full-screen generation overlay (Zavo-branded). Drives the wow animation
// shown while a page generates, then opens the page when ready.
genOverlay: { pageName: string; message: string; progress: number; status: 'generating' | 'done' | 'failed'; usage?: BlueprintUsage } | null = null;

// Generation tracker
generationTasks: Map<string, GenerationTask> = new Map();
private trackerSub: Subscription | null = null;

constructor(
  public pageService: PageService,
  private router: Router,
  public entityService: EntityService,
  public globalVariablesService: GlobalVariablesService,
  private blueprintContextBuilder: BlueprintContextBuilder,
  private blueprintService: BlueprintService,
  private clientService: ClientService,
  public tracker: AIGenerationTrackerService,
) {
  this.pageService.$values.subscribe((value: any) => {
    this.pageList = value;
    this.pageListArrived = true;
    this.applyFilter();
  });
}

ngOnInit(): void {
  this.trackerSub = this.tracker.tasks$.subscribe(tasks => {
    this.generationTasks = tasks;
  });
  this.tracker.restoreSubscriptions(this.clientService.selectedClientId);
}

ngOnDestroy(): void {
  this.trackerSub?.unsubscribe();
}

getGenerationTask(pageName: string): GenerationTask | undefined {
  return this.generationTasks.get(pageName);
}

getPendingGenerations(): GenerationTask[] {
  const pageNames = new Set(this.filteredPageList.map(p => p.name));
  const pending: GenerationTask[] = [];
  for (const [name, task] of this.generationTasks) {
    if (!pageNames.has(name) && (task.status === 'generating' || task.status === 'failed')) {
      pending.push(task);
    }
  }
  return pending;
}

trackByTaskName = (_: number, task: GenerationTask) => task.pageName;

/** Template-callable: hides the Delete button on a platform page. */
isProtectedPage = isProtectedPage;

/** The tracker keys its tasks by page name, so that name is the task's id. */
private failedTasks(): GenerationTask[] {
  return [...this.generationTasks.values()].filter((t) => t.status === 'failed');
}

dismissFailedTask(pageName: string): void {
  this.tracker.removeTask(pageName);
}

// Filter methods
setFilter(filterType: string): void {
  this.selectedFilter = filterType;
  this.applyFilter();
}

applyFilter(): void {
  let list = this.pageList;
  if (this.selectedFilter !== 'all') {
    list = list.filter(page => page.type === this.selectedFilter);
  }
  if (this.searchTerm.trim()) {
    const term = this.searchTerm.trim().toLowerCase();
    list = list.filter(page => page.name.toLowerCase().includes(term));
  }
  this.filteredPageList = list;
  this.unwiredByPage = {};
  for (const page of list) {
    const count = pageUnwiredCount(page);
    if (count) this.unwiredByPage[(page as any).id] = count;
  }
  this.previewIndex = 0;
}

staggerDelay(index: number): string {
  return (index * 0.07) + 's';
}

barWidth(count: number, scale: number): string {
  return (count > 0 ? Math.min(count * scale, 100) : 4) + '%';
}

complexityPercent(page: Page): number {
  const raw = (page.functions?.length || 0) + (page.sections?.length || 0) * 3 + (page.entites?.length || 0) * 2;
  return Math.min(raw * 2, 100);
}

get previewPage(): Page | null {
  if (!this.filteredPageList?.length) return null;
  const idx = Math.min(this.previewIndex, this.filteredPageList.length - 1);
  return this.filteredPageList[idx];
}

get safePreviewIndex(): number {
  if (!this.filteredPageList?.length) return 0;
  return Math.min(this.previewIndex, this.filteredPageList.length - 1);
}

getPagesByType(type: string): Page[] {
  return this.filteredPageList.filter(p => p.type === type);
}

getOriginalIndex(page: Page): number {
  return this.filteredPageList.indexOf(page);
}

toggleExpand(index: number): void {
  this.expandedIndex = this.expandedIndex === index ? -1 : index;
}

getPageInitials(name: string): string {
  if (!name) return '?';
  const parts = name.replace(/([a-z])([A-Z])/g, '$1 $2').split(/[\s_-]+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

toggleGroup(type: string): void {
  if (this.collapsedGroups.has(type)) {
    this.collapsedGroups.delete(type);
  } else {
    this.collapsedGroups.add(type);
  }
}

isGroupCollapsed(type: string): boolean {
  return this.collapsedGroups.has(type);
}

getBuildingHeight(page: Page): number {
  const raw = (page.sections?.length || 0) * 10 + (page.functions?.length || 0) * 3 + (page.entites?.length || 0) * 5;
  return Math.max(80, Math.min(Math.round(80 + Math.sqrt(raw) * 30), 440));
}

// Count variables in a page's localVariable object
countVariables(localVariable: any): number {
  if (!localVariable) return 0;
  // Count all properties except defaultFlag, defaultString, and defaultNumber
  return Object.keys(localVariable).filter(key => 
    !['defaultFlag', 'defaultString', 'defaultNumber', 'defaultTimeStamp'].includes(key)
  ).length;
}

async createPage() {
  const refusal = await this.runCreatePage();
  if (refusal) alert(refusal);
}
/** The Create button's rules and work, with a refusal handed back: a dialog would freeze the screen on a call from do_action. */
private async runCreatePage(): Promise<string | null> {
  this.newPageName = this.newPageName.replace(/\s/g, '');
 var flag = this.checkPageName();
 if(flag && this.newPageName !== '') {
  // Build the page (with its default "main" section) from the shared factory
  // so a hand-created page matches an auto-provisioned one.
  this.pageService.selectedPage = buildDefaultPage(this.newPageType, this.newPageName);
  // Persist the new page immediately so it's stored on creation,
  // not only when the user manually saves later.
  await this.pageService.create(this.pageService.selectedPage);
  await this.router.navigate(['/ui-editor']);
  this.applyFilter(); // After creating a page, update filters
  return null;
 } else {
  if(this.newPageName == '') {

    return "Page Name cannot be blank";
  } else {
  return "Please Change Page Name as same name exists";
  }
 }
}
private newPageHalfFilled(): boolean {
  return this.showCreateModal && this.newPageName.trim() !== '';
}
private async createPageFor(values: Record<string, unknown>): Promise<ScreenActionAnswer> {
  if (this.newPageHalfFilled()) return { refused: 'The builder is filling in a new page; ask them to finish or cancel it first. Nothing was changed.' };
  if (!this.pageListArrived) return { refused: 'The page list is still loading. Nothing was changed; try again in a moment.' };
  const type = String(values['newPageType'] ?? this.newPageType);
  if (!['web', 'pdf', 'mobile'].includes(type)) return { refused: 'newPageType takes web, pdf or mobile. Nothing was changed.' };

  const before = { name: this.newPageName, type: this.newPageType };
  this.newPageName = String(values['newPageName'] ?? '');
  this.newPageType = type;
  let refusal: string | null;
  try {
    refusal = await this.runCreatePage();
  } catch (e) {
    this.newPageName = before.name;
    this.newPageType = before.type;
    throw e;
  }
  if (refusal) {
    this.newPageName = before.name;
    this.newPageType = before.type;
    return { refused: `${refusal}. Nothing was changed.` };
  }
  const page = this.pageService.selectedPage;
  return { done: `Created the page ${page.name} and opened it.`, result: { page: { id: page.id, name: page.name, type: page.type } } };
}
checkPageName() {
for(let i=0; i < this.pageList.length; i++) {
  if(this.pageList[i].name == this.newPageName) {
    return false;
  }
}
  return true;
}

// These functions need to be updated to work with the filtered list index
async editPage(index: number) {
  const selectedPage = this.filteredPageList[index];
  const originalIndex = this.pageList.findIndex(page => page.id === selectedPage.id);

  this.pageService.selectedPage = this.pageList[originalIndex];
  this.pageService.entryMode = 'preview';
  await this.pageSelected();
  this.router.navigate(['/ui-editor']);
}

async enterSpotlight(index: number) {
  const selectedPage = this.filteredPageList[index];
  const originalIndex = this.pageList.findIndex(page => page.id === selectedPage.id);

  this.pageService.selectedPage = this.pageList[originalIndex];
  this.pageService.entryMode = 'spotlight';
  await this.pageSelected();
  this.router.navigate(['/ui-editor']);
}

async pageData(index: number) {
  const selectedPage = this.filteredPageList[index];
  const originalIndex = this.pageList.findIndex(page => page.id === selectedPage.id);
  
  this.pageService.selectedPage = this.pageList[originalIndex];
  await this.pageSelected();
this.router.navigate(['/dashboard/pageData']);
}

async pageFunctions(index: number) {
  const selectedPage = this.filteredPageList[index];
  const originalIndex = this.pageList.findIndex(page => page.id === selectedPage.id);
  
  this.pageService.selectedPage = this.pageList[originalIndex];
  const flag = await this.pageSelected();
  if(flag) {
  this.router.navigate(['/dashboard/function-editor']);
  }
}

async pageSelected() {
try {
  const value: Entity[] = await firstValueFrom(this.entityService.$values);
      await this.pageService.populateEntityListsForSelectedPage(value, true); // STEP 6 (2026-07-17): consolidated build in PageService; old body commented below.
  // this.pageService.newEntityListForSelected = [];
  // this.pageService.newEntityTransformedListForSelected = [];
  // 
// 
    // this.pageService.localEntityList = [];
    // this.pageService.entityListReferenceEntity = [];
  // 
  // // GATE REINTRODUCED: only the page's imported entities (page.entites) are loaded/exposed
  // // for bindings / multiData / Atlas. (Was all-entities from 2026-06-10 fdb18c2; reverted here.)
  // for (let j = 0; j < this.pageService.selectedPage.entites.length; j++) {
    // for (let i = 0; i < value.length; i++) {
      // if (value[i].id == this.pageService.selectedPage.entites[j]) {
        // this.pageService.newEntityListForSelected.push(structuredClone(value[i]));
        // const data: {entityId: string ,id: string , name: string , data: any} = {
          // entityId: value[i].id,
          // id: value[i].id,
          // name: value[i].name,
          // data: await this.pageService.createEntityData(this.getEntityById(value[i].id, value).data, [], value).then((returnedData:any) => {
            // return returnedData;
          // })
        // };
// 
        // this.pageService.newEntityTransformedListForSelected.push(structuredClone(data));
        // const returnedEntityListReferenceEntity = this.addEntityToEntityListRecursively(value[i].data, structuredClone(this.pageService.newEntityTransformedListForSelected) , true, 0, {name: 'systemNAN', id: '', data: {}}, value);
        // this.pageService.entityListReferenceEntity.push(...returnedEntityListReferenceEntity);
        // this.pageService.newEntityTransformedListForSelected = this.addEntityToEntityListRecursively(value[i].data, structuredClone(this.pageService.newEntityTransformedListForSelected), false, 0, {name: 'systemNAN', id: '', data: {}}, value);
      // }
    // }
  // }
  // // --- Superseded (all-entities, no gate) — kept per comment-don't-delete:
  // // for (let i = 0; i < value.length; i++) {
  // //   this.pageService.newEntityListForSelected.push(structuredClone(value[i]));
  // //   const data: {entityId: string ,id: string , name: string , data: any} = {
  // //     entityId: value[i].id,
  // //     id: value[i].id,
  // //     name: value[i].name,
  // //     data: await this.pageService.createEntityData(this.getEntityById(value[i].id, value).data, [], value).then((returnedData:any) => {
  // //       return returnedData;
  // //     })
  // //   };
  // //   this.pageService.newEntityTransformedListForSelected.push(structuredClone(data));
  // //   const returnedEntityListReferenceEntity = this.addEntityToEntityListRecursively(value[i].data, structuredClone(this.pageService.newEntityTransformedListForSelected) , true, 0, {name: 'systemNAN', id: '', data: {}}, value);
  // //   this.pageService.entityListReferenceEntity.push(...returnedEntityListReferenceEntity);
  // //   this.pageService.newEntityTransformedListForSelected = this.addEntityToEntityListRecursively(value[i].data, structuredClone(this.pageService.newEntityTransformedListForSelected), false, 0, {name: 'systemNAN', id: '', data: {}}, value);
  // // }
// 
    // // for local entites data 
    // for(let key of Object.keys(this.pageService.selectedPage.localVariable)) {
      // if(typeof this.pageService.selectedPage.localVariable[key] == 'object') {
        // if(this.pageService.selectedPage.localVariable[key].type == 'ENTITY') {
          // // for local entites data
          // var entityData: TypeData[] = [];          
          // for (let i = 0; i < value.length; i++) {
            // if (value[i].id == this.pageService.selectedPage.localVariable[key].entityId) {
             // entityData = value[i].data;
             // break;
            // } else {
              // entityData = await this.checkEntitesRecursively(value[i].data, this.pageService.selectedPage.localVariable[key].entityId).then((returnedData:any) => {
                // return returnedData;
              // });
              // if(entityData.length !== 0) 
              // break;
            // }
          // }
          // if(entityData.length == 0)
          // throw new Error('Selected entities are not defined. - its a weird error should not occur - check the config something is missing or has beein deleted');
          // const createdData = await this.pageService.createEntityData(entityData, [], value).then((returnedData:any) => {
            // return returnedData;
          // });
// 
          // this.pageService.localEntityList.push({ name: 'localVariable - ' + key, id: 'local_variable%' + key,
           // data: createdData
        // });
        // this.pageService.localEntityList = this.addEntityToEntityListRecursively(entityData, structuredClone(this.pageService.localEntityList), false, 0, {name: 'systemNAN', id: '', data: {}}, value);
        // this.pageService.selectedPage.localVariable[key].data = createdData;
        // }
        // // ── DEAD BRANCH (was `&& false`, never ran) — commented + kept per convention ──
        // // else if(this.pageService.selectedPage.localVariable[key].type == 'ARRAY_OF_OBJECT' && false) { // shouldnot run as the we want ntg in a listof array when it first
          // // // for local entites data
          // // var entityData: TypeData[] = [];
          // // for (let i = 0; i < value.length; i++) {
            // // if (value[i].id == this.pageService.selectedPage.localVariable[key].entityId) {
             // // entityData = value[i].data;
             // // break;
            // // } else {
              // // entityData = await this.checkEntitesRecursively(value[i].data, this.pageService.selectedPage.localVariable[key].entityId).then((returnedData:any) => {
                // // return returnedData;
              // // });
              // // if(entityData.length !== 0) 
              // // break;
            // // }
          // // }
          // // if(entityData.length == 0)
          // // throw new Error('Selected entities are not defined. - its a weird error should not occur - check the config something is missing or has beein deleted');
          // // const createdData = [await this.pageService.createEntityData(entityData).then((returnedData:any) => {
            // // return returnedData;
          // // })];
          // // this.pageService.localEntityList.push({ name: 'localVariable - ' + key, id: 'local_entity$' + key,
           // // data: createdData
        // // });
// // 
        // // this.pageService.selectedPage.localVariable[key].data = createdData;
        // // }
      // }
    // }
           // // end of local entity data
// 
           // this.pageService.globalEntityList = [];
    // // for global entites data 
    // for(let key of Object.keys(this.globalVariablesService.globalVariable)) {
      // if(typeof this.globalVariablesService.globalVariable[key] == 'object') {
        // if(this.globalVariablesService.globalVariable[key].type == 'ENTITY') {
          // // for gloabl entites data
          // var entityData: TypeData[] = [];
          // for (let i = 0; i < value.length; i++) {
            // if (value[i].id == this.globalVariablesService.globalVariable[key].entityId) {
             // entityData = value[i].data;
             // break;
            // } else {
              // entityData = await this.checkEntitesRecursively(value[i].data, this.globalVariablesService.globalVariable[key].entityId).then((returnedData:any) => {
                // return returnedData;
              // });
              // if(entityData.length !== 0) 
              // break;
            // }
          // }
          // if(entityData.length == 0)
          // throw new Error('Selected entities are not defined. - its a weird error should not occur - check the config something is missing or has beein deleted');
          // const createdData = await this.pageService.createEntityData(entityData, [], value).then((returnedData:any) => {
            // return returnedData;
          // });
          // this.pageService.globalEntityList.push({ name: 'globalVariable - ' + key, id: 'global_variable%' + key,
           // data: createdData
        // });
        // this.pageService.globalEntityList = this.addEntityToEntityListRecursively(entityData, structuredClone(this.pageService.globalEntityList), false, 0, {name: 'systemNAN', id: '', data: {}}, value);
        // this.globalVariablesService.globalVariable[key].data = createdData;
        // }
        // else if(this.globalVariablesService.globalVariable[key].type == 'ARRAY_OF_OBJECT' && false) {  // shouldnot run as the we want ntg in a listof array when it first appiers
          // // for global entites data
          // var entityData: TypeData[] = [];
          // for (let i = 0; i < value.length; i++) {
            // if (value[i].id == this.globalVariablesService.globalVariable[key].entityId) {
             // entityData = value[i].data;
             // break;
            // } else {
              // entityData = await this.checkEntitesRecursively(value[i].data, this.globalVariablesService.globalVariable[key].entityId).then((returnedData:any) => {
                // return returnedData;
              // });
              // if(entityData.length !== 0) 
              // break;
            // }
          // }
          // if(entityData.length == 0)
          // throw new Error('Selected entities are not defined. - its a weird error should not occur - check the config something is missing or has beein deleted');
          // const createdData = [await this.pageService.createEntityData(entityData).then((returnedData:any) => {
            // return returnedData;
          // })];
          // this.pageService.globalEntityList.push({ name: 'globalVariable - ' + key, id: 'global_entity$' + key,
           // data: createdData
        // });
// 
        // this.globalVariablesService.globalVariable[key].data = createdData;
        // }
      // }
    // }
           // // end of global entity data
  // 
  return true;
} catch (error) {
  console.error('Error fetching data:', error);
  return false;
}
}

// addEntityToEntityListRecursively(entityData: TypeData[], list : {name: string , id: string , data: any}[], arrayOfObjectsFlag: boolean = false , indexForConsideringList: number = 0, parentList : {name: string , id: string , data: any} = {name: 'systemNAN' , id: '' , data:{}}, allEntities: Entity[] = []) {
  // const firstList = structuredClone(list[list.length -1]);
  // if(parentList.name == 'systemNAN') {
    // parentList = structuredClone(firstList);
  // }
  // if(indexForConsideringList == 0) {
  // var entityListReferenceEntity: any = [];
// 
  // } else {
  // var entityListReferenceEntity: any = list;
// 
  // }
  // indexForConsideringList++;
    // for(let i =0 ; i < entityData.length ; i++) {
      // if(!arrayOfObjectsFlag && entityData[i].type == 'ENTITY_REFERENCE') {
        // list.push({name: firstList.name + '-' + entityData[i].name , id: firstList.id + '%' + entityData[i].name , data: firstList.data[entityData[i].name]});
        // const refEntity = allEntities.find(e => e.id === entityData[i].referenceEntityName);
        // const refData = refEntity ? refEntity.data : entityData[i].data;
      // list = this.addEntityToEntityListRecursively(refData , list , false , 0 ,list[list.length - 1], allEntities);
                // } else if(arrayOfObjectsFlag && entityData[i].type == 'ARRAY_OF_OBJECT') {
            // // entityListReferenceEntity.push({name: list[list.length -1].name + '-' + entityData[i].name , id: list[list.length -1].id + '%' + entityData[i].name , data: list[list.length -1].data[entityData[i].name][0], extendedAttributes: entityData[i].extendedAttributes? true:false});
             // entityListReferenceEntity.push({name: parentList.name + '-' + entityData[i].name , id: parentList.id + '%' + entityData[i].name , data: parentList.data[entityData[i].name][0], extendedAttributes: entityData[i].extendedAttributes? true:false});
          // entityListReferenceEntity = this.addEntityToEntityListRecursively(entityData[i].data , entityListReferenceEntity , true , indexForConsideringList , {name: parentList.name + '-' + entityData[i].name , id: parentList.id + '%' + entityData[i].name , data: parentList.data[entityData[i].name][0]}, allEntities);
          // }
    // }
    // if(arrayOfObjectsFlag) {
      // return entityListReferenceEntity;
    // } else {
  // return list;
    // }
// }

// async checkEntitesRecursively(entityData: TypeData[], entityId: string) {
  // var array: TypeData[] = []
  // for(let variable of entityData) {
    // if(variable.type == 'ARRAY_OF_OBJECT') {
      // if(variable.id == entityId)
      // return variable.data
      // array= await this.checkEntitesRecursively(variable.data, entityId).then((returnedData:any) => {
        // return returnedData;
      // });
      // if(array.length !== 0)
      // break;
    // }
  // }
  // return array;
// }
// getEntityById(entityId: string, value: Entity[]) {
  // var entityToBeReturned: Entity = {
    // id: '',
    // name: '',
    // type: '',
    // status: 'not_found',
    // data: [],
  // };
  // var returnedData: TypeData = {
    // name: '',
    // type: '',
    // isUnique: false,
    // primaryKey: false,
    // startIncrementFrom: 0,
    // referenceEntityName: '',
    // referenceEntityColumnName: '',
    // id: 'not_found',
    // data: [],
    // defaultValue: '',
  // };
  // for (let entity of this.pageService.newEntityListForSelected) {
    // if (entity.id == entityId) {
      // entity.data = this.completeEntityWithEntityReferences(structuredClone(entity.data), [entity.id], value);
      // return entity;
    // }
    // returnedData = this.recursivelyGetEntityById(entity.data, entityId);
    // if (returnedData.id !== 'not_found') {
      // entityToBeReturned.id = returnedData.id;
      // entityToBeReturned.name = returnedData.name;
      // entityToBeReturned.type = returnedData.type;
      // entityToBeReturned.status = 'READY';
      // entityToBeReturned.data =  this.completeEntityWithEntityReferences(structuredClone(returnedData.data), [returnedData.id], value);
      // entityToBeReturned.data = returnedData.data;
      // return entityToBeReturned;
    // }
  // }
  // return entityToBeReturned;
// }
  // ── standalone getEntityById commented 2026-07-17: consolidated onto PageService.getEntityById
  //    (merged: structuredClone cyclic-ref fix + entity.status fix). Delegate below. ──
  // Was (2026-10-29): value defaulted to the page's imported entities only; the service now resolves over the full list.
  getEntityById(entityId: string , value?: Entity[]) {
   
    return this.pageService.getEntityById(entityId , value);
  }
// recursivelyGetEntityById(entity: TypeData[], entityId: string) {
  // var data: TypeData = {
    // name: '',
    // type: '',
    // isUnique: false,
    // primaryKey: false,
    // startIncrementFrom: 0,
    // referenceEntityName: '',
    // referenceEntityColumnName: '',
    // id: 'not_found',
    // data: [],
    // defaultValue: '',
  // };
  // for (let variable of entity) {
    // if (variable.type == 'ARRAY_OF_OBJECT') {
      // if (variable.id == entityId) {
        // var valueToBeReturned = structuredClone(variable);
        // return valueToBeReturned;
        // // return variable;
      // }
     // data = this.recursivelyGetEntityById(variable.data, entityId);
     // if(data.id !== 'not_found') {
      // return data;
     // }
    // }
  // }
// 
  // return data;
// }

// completeEntityWithEntityReferences(entity: TypeData[], parentEntites: string[], value: Entity[]) {
  // for(let variable of entity) {
    // if(variable.type == 'ENTITY_REFERENCE') {
      // var flag = true;
      // for(let entityId of parentEntites) {
        // if(entityId == variable.referenceEntityName) {
          // flag = false;
          // break;
        // }
      // }
      // if(flag) {
        // for(let i=0 ; i < value.length ; i++) {
          // if(value[i].id == variable.referenceEntityName) {
            // variable.data = this.completeEntityWithEntityReferences(value[i].data, [...parentEntites , variable.referenceEntityName], value)
          // }
        // }
      // }
    // } else if (variable.type == 'ARRAY_OF_OBJECT') {
      // variable.data = this.completeEntityWithEntityReferences(variable.data, [...parentEntites], value)
    // }
  // }
// 
  // return entity;
// }

// ── AI Page Builder ──────────────────────────────────────────────────────

onScreenshotSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  if (!input.files || input.files.length === 0) return;
  const file = input.files[0];
  this.aiScreenshotName = file.name;
  const reader = new FileReader();
  reader.onload = async () => {
    // Downscale large screenshots up front. The model downscales to ~1568px internally
    // anyway, so this is lossless vs what it sees — but it keeps the request body small
    // (large attachments were failing).
    const dataUrl = await this.downscaleImage(reader.result as string, 1600);
    this.aiScreenshot = dataUrl.split(',')[1];
  };
  reader.readAsDataURL(file);
}

/** Shrink an image so its largest side is <= maxDim (no-op if already small / on error). */
private async downscaleImage(dataUrl: string, maxDim: number): Promise<string> {
  try {
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    if (!longest || longest <= maxDim) return dataUrl;
    const scale = maxDim / longest;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } catch {
    return dataUrl;
  }
}

async startAIGeneration() {
  if (!this.aiScreenshot || !this.aiPageName) return;

  const pageName = this.aiPageName.replace(/\s/g, '');
  const clientId = this.clientService.selectedClientId;
  // Snapshot the inputs before we clear the modal fields below.
  const screenshot = this.aiScreenshot;
  const description = this.aiPrompt || undefined;
  this.aiAbortController = new AbortController();
  const signal = this.aiAbortController.signal;

  // Instant feedback: close the modal and show the progress card NOW, then fire the
  // request — so the user isn't staring at a frozen modal.
  this.showAIModal = false;
  this.resetAIModalFields();
  this.tracker.startPending(pageName);
  this.genOverlay = { pageName, message: 'Preparing…', progress: 3, status: 'generating' };

  let finished = false;
  const finish = async (pageId?: string) => {
    if (finished) return;
    finished = true;
    this.tracker.markCompleted(pageName);
    const results = await this.pageService.query(1000);
    // If the user sent it to the background, just let the tracker card finish — don't hijack.
    if (this.genOverlay?.pageName !== pageName) return;
    this.genOverlay = { ...this.genOverlay, status: 'done', progress: 100, message: 'Ready — opening…' };
    const list: any[] = (results as any[]) || this.pageList;
    const page = list.find((p) => p.id === pageId) || list.find((p) => p.name === pageName);
    setTimeout(() => {
      if (this.genOverlay?.pageName !== pageName) return; // dismissed during the flourish
      this.genOverlay = null;
      if (page) {
        this.pageService.selectedPage = page;
        this.router.navigate(['/ui-editor']);
      }
    }, 1300);
  };

  // The page registry, globals and data model. WITHOUT THIS the CF's `interactivity: !!context` gate
  // is off: no registry reaches the model, the prompt still calls it complete, and every cross-page
  // control comes back `inert` (measured 2026-08-20 — 20 dead controls across 6 regenerated pages).
  const context = await this.blueprintContextBuilder.build(this.pageList, pageName, description);

  this.blueprintService.streamIterate(
    {
      clientId,
      pageName,
      pageDescription: description,
      originalScreenshot: screenshot,
      pageType: this.blueprintPageType,
      context,
    },
    {
      onSession: (sessionId) => {
        this.tracker.startTracking(pageName, sessionId, clientId);
      },
      onProgress: (phase, message) => {
        this.tracker.updateProgress(pageName, phase, message);
        const t = this.tracker.tasks$.value.get(pageName);
        if (this.genOverlay?.pageName === pageName) {
          this.genOverlay = { ...this.genOverlay, message, progress: t?.progress ?? this.genOverlay.progress };
        }
      },
      onResult: (data) => finish(data?.pageId),
      onUsage: (usage) => {
        this.tracker.setUsage(pageName, usage);
        if (this.genOverlay?.pageName === pageName) {
          this.genOverlay = { ...this.genOverlay, usage };
        }
      },
      onError: (message) => {
        this.tracker.markFailed(pageName, message);
        if (this.genOverlay?.pageName === pageName) {
          this.genOverlay = { ...this.genOverlay, status: 'failed', message };
        }
      },
    },
    signal,
  );
}

/** Hide the cinematic overlay but keep generating in the background (tracker card carries on). */
dismissOverlay() {
  this.genOverlay = null;
}

resetAIModalFields() {
  this.aiScreenshot = '';
  this.aiScreenshotName = '';
  this.aiPageName = '';
  this.aiPrompt = '';
}

cancelAIGeneration() {
  this.aiAbortController?.abort();
  this.aiAbortController = null;
}

openDeleteModal(index: number) {
  const target = this.filteredPageList[index];
  // Platform pages (defaultPage, defaultMobilePage, landingPage) are undeletable. The guard is the
  // RESERVED ID LIST, not `isSystem` — see PROTECTED_PAGE_IDS in page-defaults.ts for why.
  if (isProtectedPage(target)) {
    alert('This is a platform page and cannot be deleted.');
    return;
  }
  this.deleteTarget = target;
  this.deleteConfirmText = '';
  this.deleteLoading = false;
}

async confirmDeletePage() {
  if (!this.deleteTarget || this.deleteConfirmText !== this.deleteTarget.name) return;
  if (isProtectedPage(this.deleteTarget)) return; // platform pages are undeletable

  const { id, name } = this.deleteTarget;
  this.deleteLoading = true;
  this.waits.saving('delete-page');
  try {
    await this.pageService.deletePage(id);
    this.deleteTarget = null;
    this.deleteConfirmText = '';
    this.waits.report('delete-page', { outcome: 'saved', saved: { id, name } });
  } catch (e) {
    console.error('Delete failed:', e);
    this.waits.report('delete-page', { outcome: 'failed', reason: e instanceof Error ? e.message : String(e) });
    alert('Failed to delete page. Please try again.');
  }
  this.deleteLoading = false;
}

cancelDelete() {
  this.deleteTarget = null;
  this.deleteConfirmText = '';
  this.deleteLoading = false;
  this.waits.report('delete-page', { outcome: 'discarded' });
}

/** Opens the page's own delete form with the name typed in. The typed name guards a person's slip, so filling it costs nothing: the builder's click is still the commit. */
private async prefillDelete(values: Record<string, unknown>): Promise<Prefilled> {
  const target = this.pageList.find((p) => p.id === values['pageId']);
  if (!target) return { refused: 'No page has that id; read_screen lists them under pages. Nothing was changed.' };
  if (isProtectedPage(target)) return { refused: `${target.name} is a platform page and can't be deleted. Nothing was changed.` };
  const typed = values['deleteConfirmText'] ?? target.name;
  if (typed !== target.name) return { refused: `The confirmation has to be the page's exact name, ${target.name}. Nothing was changed.` };
  if (this.deleteTarget && (this.deleteConfirmText.trim() || this.deleteLoading)) {
    return { refused: `The builder has the delete form open for ${this.deleteTarget.name}. Nothing was changed. Ask them to finish or close it, then try again.` };
  }
  this.deleteTarget = target;
  this.deleteConfirmText = target.name;
  this.deleteLoading = false;
  return {};
}

resetAIModal() {
  this.showAIModal = false;
  this.aiScreenshot = '';
  this.aiScreenshotName = '';
  this.aiPageName = '';
  this.aiPrompt = '';
  this.aiAbortController = null;
}

}


